import { useForm } from "../../../../hooks/useForm/useForm";
import { FormProps } from "../../../form-types";
import { Database } from "../../../../../../lib/supabase";
import {
	PatientEntityType,
	SupabaseTableEnum,
} from "../../../../../../lib/supabase/supabaseTypes";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { showNotification } from "@/dentlab/src/store/Central/selectors";
import { FormSectionWithAutosave } from "@/dentlab/src/components/dialog-components/form-section-with-autosave/form-section-with-autosave.component";
import { PatientFormContext } from "./PatientFormContext";
import { usePatientStore } from "@/dentlab/src/store/Patient";
import { Dialog, DialogContent } from "@/dentlab/src/components/ui/dialog";
import { Button } from "@/dentlab/src/components/ui/button";

export interface PatientFormProps extends FormProps {
	patientId?: string;
	openExtended?: boolean;
}

export const PatientForm: React.FC<
	PatientFormProps & {
		setIsPendingChanges?: (isPendingChanges: boolean) => void;
		withinSheet?: boolean;
		autosave?: boolean;
		className?: string;
		children: React.ReactNode;
	}
> = ({
	onSave = (patient: PatientEntityType) => { },
	submitRef = null,
	setIsPendingChanges = () => { },
	patientId = undefined,
	openExtended = false,
	withinSheet = false,
	autosave = true,
	children,
	className = "",
}) => {
		const supabaseTable = SupabaseTableEnum.PATIENTS;

		const { upsertPatient } = usePatientStore((state) => ({
			upsertPatient: state.upsertPatient,
		}));

		const { organizationId } = useCentralStore((state) => ({
			organizationId: state.organization?.id,
		}));

		const useFormCustomFields = {
			organization_id: organizationId,
		};
		const isUpdate = patientId ? true : false;

		const useFormFetchData = isUpdate ? true : false;
		const useFormFetchId = isUpdate ? patientId : undefined;

		const {
			handleInputChange,
			handleValueChange,
			handleCheckboxChange,
			handleCreate,
			handleUpdate,
			formData,
			formErrors,
			setFormErrors,
			isPendingChanges,
		} = useForm<Database["public"]["Tables"]["patients"]["Row"]>(
			useFormCustomFields,
			supabaseTable,
			useFormFetchData,
			useFormFetchId,
			"Patient",
			setIsPendingChanges
		);

		const handleUpdateSubmit = async (
			event: React.FormEvent<HTMLFormElement>
		) => {
			return await handleUpdate(event, patientId, { hideNotification: true });
		};

		const handleInsertSubmit = async (
			event: React.FormEvent<HTMLFormElement>
		) => {
			return await handleCreate(event);
		};

		const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
			// Search vector field can only be set by the database
			delete formData.search_vector;
			const { data, status } = isUpdate
				? await handleUpdateSubmit(event)
				: await handleInsertSubmit(event);
			if (data) {
				const patient = data[0];
				upsertPatient(patient);
				onSave(patient);
			} else {
				if (!formData.code) {
					showNotification({
						message:
							"Möglicherweise konnte kein eindeutiger Code generiert werden. Bitte geben Sie einen Code manuell ein.",
						type: "warning",
					});
				} else if (!status) {
					showNotification({
						message: "Fehler beim Speichern des Patienten.",
						type: "error",
					});
				}
			}
		};

		return (
			<PatientFormContext.Provider
				value={{
					onSave,
					submitRef,
					setIsPendingChanges,
					patientId,
					openExtended,
					withinSheet,
					formData,
					formErrors,
					setFormErrors,
					handleInputChange,
					handleValueChange,
					handleCheckboxChange,
					isUpdate,
				}}
			>
				{autosave ? (
					<FormSectionWithAutosave
						onSubmit={onSubmit}
						formData={formData}
						isPendingChanges={isPendingChanges}
						formErrors={formErrors}
						className={className}
					>
						{children}
					</FormSectionWithAutosave>
				) : (
					<form onSubmit={onSubmit}>
						{children}
						<Button type="submit" className="mt-4">Speichern</Button>
					</form>
				)}
			</PatientFormContext.Provider>
		);
	};
